import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, toRef } from 'vue'

import TransportationEmojiLabelField from '../TransportationEmojiLabelField.vue'
import RouteManager from '../RouteManager.vue'
import UseForSection from '../UseForSection.vue'
import LegPickerSheet from '../LegPickerSheet.vue'
import RentalManager from './driving/RentalManager.vue'
import DocumentManager from '@/components/trip/documents/manager/DocumentManager.vue'
import TransportationLocationSelector from '@/components/trip/transportations/TransportationLocationSelector.vue'
import TransportationDateTimeRangePicker from '@/components/trip/transportations/TransportationDateTimeRangePicker.vue'
import FormGroup from '@/components/core/form/FormGroup.vue'
import LTextarea from '@/components/core/form/Textarea.vue'
import { useSelectedTrip } from '@/composables/trip'
import { useTransportationDocuments } from '@/composables/transportation'
import {
  useTransportationGroups,
  useTransportationReuseEnabled,
} from '@/composables/transportation-group'
import {
  type GroupAnchorDraft,
  useTransportationStore,
} from '@/stores/transportation'
import { useTransportationGroupStore } from '@/stores/transportation-group'
import { DrivingTransportationUIInput } from '@/types/transportation'
import { regularFromWaypointIds } from '@/utils/transportation-group-sync'


export default /*#__PURE__*/_defineComponent({
  __name: 'DrivingForm',
  props: {
    transportation: null,
    waypointId: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  transportation: DrivingTransportationUIInput
  waypointId: string
};



const trip = useSelectedTrip()
const transportationStore = useTransportationStore()
const transportation = toRef(props, 'transportation')

const reuseEnabled = useTransportationReuseEnabled()

useTransportationGroups(computed(() => trip.value._id))

const idPrefix = computed(() => {
  return `transportation-${transportation.value._id ?? 'driving-new'}`
})

// Document Management ---------------------------------------------------------

const managedDocuments = useTransportationDocuments(transportation)

// Show/Hide More Content ------------------------------------------------------

const hasContentInMoreSection = !!(
  managedDocuments.value.length > 0 || transportation.value.comment
)

const showContent = ref(hasContentInMoreSection)

function toggleMoreContent() {
  showContent.value = !showContent.value
}

const groupStore = useTransportationGroupStore()
const { selectedTransportation } = storeToRefs(transportationStore)

const legPickerOpen = ref(false)
const legPickerSheetRef = ref<InstanceType<typeof LegPickerSheet> | null>(null)

const existingGroup = computed(() =>
  groupStore.getGroupForTransportation(transportation.value),
)

const lastWaypointId = computed(() => {
  const wps = trip.value?.waypoints ?? []
  return wps[wps.length - 1]?._id
})

const seedIsArrival = computed(
  () => !!transportation.value.isArrivalTransportation,
)

/**
 * The edited transportation is the group's departure anchor:
 * a non-arrival, group-attached driving leg living on the last waypoint.
 */
const seedIsDeparture = computed(
  () => !seedIsArrival.value && props.waypointId === lastWaypointId.value,
)

const groupSelection = computed<string[]>(() => {
  const draft = selectedTransportation.value?.groupSelection
  if (draft?.touched) {
    return draft.value
  }
  const regular = regularFromWaypointIds(
    existingGroup.value?.fromWaypointIds ?? [],
    lastWaypointId.value,
  )
  if (
    props.waypointId &&
    !existingGroup.value &&
    !seedIsArrival.value &&
    !seedIsDeparture.value
  ) {
    return [...regular, props.waypointId]
  }
  return regular
})

/** Form-side origin/destination names. Used to label the seed-locked anchor
 * card when creating an arrival/departure transportation directly. */
const seedOriginName = computed(() => transportation.value.origin?.title)
const seedDestinationName = computed(
  () => transportation.value.destination?.title,
)

const arrivalDraft = computed<GroupAnchorDraft>(() => {
  const draft = selectedTransportation.value?.groupArrival
  if (draft?.touched) {
    return draft.value
  }
  return {
    enabled:
      seedIsArrival.value || !!existingGroup.value?.hasArrivalTransportation,
  }
})

const departureDraft = computed<GroupAnchorDraft>(() => {
  const draft = selectedTransportation.value?.groupDeparture
  if (draft?.touched) {
    return draft.value
  }
  return {
    enabled:
      seedIsDeparture.value ||
      (existingGroup.value
        ? groupStore.hasDepartureTransportation(existingGroup.value)
        : false),
  }
})

function openLegPicker() {
  legPickerOpen.value = true
}

async function validateLegPickerAnchors() {
  const isValid = legPickerSheetRef.value?.validateAnchors() ?? true
  if (!isValid) {
    legPickerOpen.value = true
    await nextTick()
  }
  return isValid
}

expose({
  validateLegPickerAnchors,
})

function onPickerInput(selection: string[]) {
  transportationStore.setGroupSelection(selection)
}

function onPickerArrival(draft: GroupAnchorDraft) {
  transportationStore.setGroupArrival(draft)
}

function onPickerDeparture(draft: GroupAnchorDraft) {
  transportationStore.setGroupDeparture(draft)
}

return { __sfc: true,props, trip, transportationStore, transportation, reuseEnabled, idPrefix, managedDocuments, hasContentInMoreSection, showContent, toggleMoreContent, groupStore, selectedTransportation, legPickerOpen, legPickerSheetRef, existingGroup, lastWaypointId, seedIsArrival, seedIsDeparture, groupSelection, seedOriginName, seedDestinationName, arrivalDraft, departureDraft, openLegPicker, validateLegPickerAnchors, onPickerInput, onPickerArrival, onPickerDeparture, TransportationEmojiLabelField, RouteManager, UseForSection, LegPickerSheet, RentalManager, DocumentManager, TransportationLocationSelector, TransportationDateTimeRangePicker, FormGroup, LTextarea }
}

})